import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Save, CheckCircle, Mic, Stethoscope,
    Heart, Thermometer, Wind, Droplets, Ruler, Scale,
} from 'lucide-react';
import { api, SoapNote } from '../lib/api';
import { VoiceRecorder } from '../components/VoiceRecorder';

const vitalFields = [
    { key: 'weightKg', label: 'Peso (kg)', icon: Scale, placeholder: '70' },
    { key: 'heightCm', label: 'Talla (cm)', icon: Ruler, placeholder: '170' },
    { key: 'bpSystolic', label: 'PA Sistólica', icon: Heart, placeholder: '120' },
    { key: 'bpDiastolic', label: 'PA Diastólica', icon: Heart, placeholder: '80' },
    { key: 'heartRate', label: 'FC (lpm)', icon: Stethoscope, placeholder: '72' },
    { key: 'tempCelsius', label: 'Temp (°C)', icon: Thermometer, placeholder: '36.5' },
    { key: 'spo2', label: 'SpO₂ (%)', icon: Droplets, placeholder: '98' },
    { key: 'respiratoryRate', label: 'FR (rpm)', icon: Wind, placeholder: '16' },
];

export function EncounterEditorPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [note, setNote] = useState<SoapNote | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [signing, setSigning] = useState(false);
    const [activeSection, setActiveSection] = useState<'S' | 'O' | 'A' | 'P'>('S');

    const [form, setForm] = useState({
        subjective: '',
        objective: '',
        assessment: '',
        plan: '',
        diagnoses: [] as Array<{ icd10Code: string; description: string; type: string }>,
        medicationsPrescribed: [] as Array<{ name: string; dose: string; frequency: string; duration: string; instructions: string }>,
        followUpDays: undefined as number | undefined,
        followUpInstructions: '',
    });

    const [vitals, setVitals] = useState<Record<string, string>>({});
    const [newDiagnosis, setNewDiagnosis] = useState({ icd10Code: '', description: '', type: 'primary' });
    const [newMed, setNewMed] = useState({ name: '', dose: '', frequency: '', duration: '', instructions: '' });

    useEffect(() => {
        if (!id) return;
        loadNote();
    }, [id]);

    async function loadNote() {
        setLoading(true);
        try {
            const res = await api.encounters.get(id!);
            setNote(res.data);
            setForm({
                subjective: res.data.subjective || '',
                objective: res.data.objective || '',
                assessment: res.data.assessment || '',
                plan: res.data.plan || '',
                diagnoses: res.data.diagnoses || [],
                medicationsPrescribed: [],
                followUpDays: undefined,
                followUpInstructions: '',
            });
            if (res.data.vitalSigns) {
                const v: Record<string, string> = {};
                for (const [k, val] of Object.entries(res.data.vitalSigns)) {
                    v[k] = String(val);
                }
                setVitals(v);
            }
        } catch {
            setNote({
                id: id!, appointmentId: 'demo', patientId: 'demo', doctorId: 'demo',
                status: 'draft', subjective: '', objective: '', assessment: '', plan: '',
                createdAt: new Date().toISOString(),
            });
        } finally {
            setLoading(false);
        }
    }

    async function handleSave() {
        setSaving(true);
        try {
            await api.encounters.update(id!, {
                ...form,
                diagnoses: form.diagnoses.length > 0 ? form.diagnoses : undefined,
                medicationsPrescribed: form.medicationsPrescribed.length > 0 ? form.medicationsPrescribed : undefined,
            } as Partial<SoapNote>);
            await loadNote();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Error al guardar');
        } finally {
            setSaving(false);
        }
    }

    async function handleSign() {
        if (!confirm('¿Firmar esta nota? Una vez firmada no podrá ser editada.')) return;
        setSigning(true);
        try {
            await api.encounters.sign(id!);
            await loadNote();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Error al firmar');
        } finally {
            setSigning(false);
        }
    }

    function addDiagnosis() {
        if (!newDiagnosis.icd10Code || !newDiagnosis.description) return;
        setForm(f => ({ ...f, diagnoses: [...f.diagnoses, { ...newDiagnosis }] }));
        setNewDiagnosis({ icd10Code: '', description: '', type: 'primary' });
    }

    function addMedication() {
        if (!newMed.name || !newMed.dose) return;
        setForm(f => ({ ...f, medicationsPrescribed: [...f.medicationsPrescribed, { ...newMed }] }));
        setNewMed({ name: '', dose: '', frequency: '', duration: '', instructions: '' });
    }

    if (loading) {
        return <div className="page-content"><div className="skeleton" style={{ height: 600 }} /></div>;
    }

    const isSigned = note?.status === 'signed';
    const soapSections = [
        { key: 'S' as const, label: 'Subjetivo', hint: 'Motivo de consulta, síntomas, historia', field: 'subjective' as const },
        { key: 'O' as const, label: 'Objetivo', hint: 'Examen físico, signos vitales, hallazgos', field: 'objective' as const },
        { key: 'A' as const, label: 'Evaluación', hint: 'Diagnóstico, impresión clínica', field: 'assessment' as const },
        { key: 'P' as const, label: 'Plan', hint: 'Tratamiento, medicamentos, seguimiento', field: 'plan' as const },
    ];

    return (
        <div className="page-content fade-in">
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-6)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                        <ArrowLeft size={16} />
                    </button>
                    <div>
                        <h1 className="page-title">Nota SOAP</h1>
                        <span className={`badge ${isSigned ? 'badge-success' : 'badge-warning'}`}>
                            {isSigned ? 'Firmada' : 'Borrador'}
                        </span>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                    {!isSigned && (
                        <>

                            <button className="btn btn-secondary" onClick={handleSave} disabled={saving}>
                                <Save size={16} /> {saving ? 'Guardando...' : 'Guardar'}
                            </button>
                            <button className="btn btn-primary" onClick={handleSign} disabled={signing}>
                                <CheckCircle size={16} /> {signing ? 'Firmando...' : 'Firmar nota'}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Voice Recorder */}
            {!isSigned && (
                <VoiceRecorder
                    encounterId={id!}
                    disabled={isSigned}
                    onSoapDraft={(draft) => {
                        setForm(f => ({
                            ...f,
                            subjective: draft.subjective || f.subjective,
                            objective: draft.objective || f.objective,
                            assessment: draft.assessment || f.assessment,
                            plan: draft.plan || f.plan,
                            diagnoses: draft.diagnoses?.length ? draft.diagnoses : f.diagnoses,
                            medicationsPrescribed: draft.medications?.length ? draft.medications : f.medicationsPrescribed,
                            followUpDays: draft.followUpDays ?? f.followUpDays,
                        }));
                    }}
                />
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 'var(--space-6)' }}>
                {/* SOAP Editor */}
                <div>
                    {/* Section Tabs */}
                    <div style={{ display: 'flex', gap: 2, marginBottom: 'var(--space-4)' }}>
                        {soapSections.map(s => (
                            <button key={s.key} onClick={() => setActiveSection(s.key)}
                                style={{
                                    flex: 1, padding: 'var(--space-3)',
                                    fontWeight: 700, fontSize: 'var(--text-lg)',
                                    borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
                                    background: activeSection === s.key ? 'var(--color-primary)' : 'var(--color-surface)',
                                    color: activeSection === s.key ? '#fff' : 'var(--color-text-secondary)',
                                    border: `1px solid ${activeSection === s.key ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                    borderBottom: activeSection === s.key ? 'none' : '1px solid var(--color-border)',
                                    transition: 'all var(--transition-fast)',
                                }}>
                                {s.key}
                            </button>
                        ))}
                    </div>

                    {/* Active Section Editor */}
                    {soapSections.filter(s => s.key === activeSection).map(section => (
                        <div key={section.key} className="card" style={{ borderTopLeftRadius: 0 }}>
                            <div style={{ marginBottom: 'var(--space-3)' }}>
                                <div style={{ fontWeight: 600, fontSize: 'var(--text-lg)' }}>{section.label}</div>
                                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{section.hint}</div>
                            </div>
                            <textarea
                                className="form-input"
                                value={form[section.field]}
                                onChange={e => setForm(f => ({ ...f, [section.field]: e.target.value }))}
                                disabled={isSigned}
                                rows={10}
                                style={{ width: '100%', resize: 'vertical', minHeight: 200, fontFamily: 'var(--font-sans)', lineHeight: 1.7 }}
                                placeholder={`Escriba ${section.label.toLowerCase()} aquí...`}
                            />
                        </div>
                    ))}

                    {/* Diagnoses */}
                    <div className="card" style={{ marginTop: 'var(--space-4)' }}>
                        <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                            Diagnósticos (CIE-10)
                        </h3>
                        {form.diagnoses.map((d, i) => (
                            <div key={i} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', marginBottom: 'var(--space-2)', padding: 'var(--space-2)', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)' }}>
                                <span className="badge badge-primary">{d.icd10Code}</span>
                                <span style={{ flex: 1, fontSize: 'var(--text-sm)' }}>{d.description}</span>
                                <span className="badge badge-neutral">{d.type}</span>
                                {!isSigned && (
                                    <button className="btn btn-icon btn-sm" onClick={() => setForm(f => ({ ...f, diagnoses: f.diagnoses.filter((_, j) => j !== i) }))}>✕</button>
                                )}
                            </div>
                        ))}
                        {!isSigned && (
                            <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
                                <input className="form-input" placeholder="CIE-10" value={newDiagnosis.icd10Code}
                                    onChange={e => setNewDiagnosis(d => ({ ...d, icd10Code: e.target.value }))} style={{ width: 100 }} />
                                <input className="form-input" placeholder="Descripción" value={newDiagnosis.description}
                                    onChange={e => setNewDiagnosis(d => ({ ...d, description: e.target.value }))} style={{ flex: 1 }} />
                                <select className="form-input" value={newDiagnosis.type}
                                    onChange={e => setNewDiagnosis(d => ({ ...d, type: e.target.value }))} style={{ width: 120 }}>
                                    <option value="primary">Primario</option>
                                    <option value="secondary">Secundario</option>
                                </select>
                                <button className="btn btn-primary btn-sm" onClick={addDiagnosis}>Agregar</button>
                            </div>
                        )}
                    </div>

                    {/* Medications */}
                    <div className="card" style={{ marginTop: 'var(--space-4)' }}>
                        <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                            Medicamentos
                        </h3>
                        {form.medicationsPrescribed.map((m, i) => (
                            <div key={i} style={{ padding: 'var(--space-3)', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-2)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontWeight: 600 }}>{m.name}</span>
                                    {!isSigned && <button className="btn btn-icon btn-sm" onClick={() => setForm(f => ({ ...f, medicationsPrescribed: f.medicationsPrescribed.filter((_, j) => j !== i) }))}>✕</button>}
                                </div>
                                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                                    {m.dose} • {m.frequency} • {m.duration}
                                </div>
                            </div>
                        ))}
                        {!isSigned && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
                                <input className="form-input" placeholder="Medicamento" value={newMed.name}
                                    onChange={e => setNewMed(m => ({ ...m, name: e.target.value }))} />
                                <input className="form-input" placeholder="Dosis" value={newMed.dose}
                                    onChange={e => setNewMed(m => ({ ...m, dose: e.target.value }))} />
                                <input className="form-input" placeholder="Frecuencia" value={newMed.frequency}
                                    onChange={e => setNewMed(m => ({ ...m, frequency: e.target.value }))} />
                                <input className="form-input" placeholder="Duración" value={newMed.duration}
                                    onChange={e => setNewMed(m => ({ ...m, duration: e.target.value }))} />
                                <input className="form-input" placeholder="Instrucciones" value={newMed.instructions}
                                    onChange={e => setNewMed(m => ({ ...m, instructions: e.target.value }))} style={{ gridColumn: '1 / -1' }} />
                                <button className="btn btn-primary btn-sm" onClick={addMedication} style={{ gridColumn: '4' }}>Agregar</button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Panel — Vitals + Follow-up */}
                <div>
                    {/* Vital Signs */}
                    <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                        <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
                            Signos Vitales
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                            {vitalFields.map(v => (
                                <div key={v.key} className="form-group" style={{ marginBottom: 0 }}>
                                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <v.icon size={12} /> {v.label}
                                    </label>
                                    <input
                                        className="form-input"
                                        type="number"
                                        step="0.1"
                                        placeholder={v.placeholder}
                                        value={vitals[v.key] ?? ''}
                                        onChange={e => setVitals(vs => ({ ...vs, [v.key]: e.target.value }))}
                                        disabled={isSigned}
                                        style={{ fontSize: 'var(--text-sm)' }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Follow-up */}
                    <div className="card">
                        <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
                            Seguimiento
                        </h3>
                        <div className="form-group">
                            <label className="form-label">Días hasta control</label>
                            <input className="form-input" type="number" min={1}
                                value={form.followUpDays ?? ''}
                                onChange={e => setForm(f => ({ ...f, followUpDays: e.target.value ? parseInt(e.target.value) : undefined }))}
                                disabled={isSigned} placeholder="30" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Instrucciones</label>
                            <textarea className="form-input" rows={3}
                                value={form.followUpInstructions}
                                onChange={e => setForm(f => ({ ...f, followUpInstructions: e.target.value }))}
                                disabled={isSigned} placeholder="Regresar si los síntomas empeoran..." />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
